import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import travelHighlightObject from '@/entities/travelHighlight/interface'

import {
  ALL_TRAVEL_EXTENSIONS_QUERY,
  SINGLE_TRAVEL_EXTENSION_QUERY,
  TRAVEL_EXTENSIONS_DEPENDENTS_QUERY,
  CREATE_TRAVEL_EXTENSION_MUTATION,
  UPDATE_TRAVEL_EXTENSION_MUTATION,
  DELETE_TRAVEL_EXTENSION_MUTATION,
  FORCE_DELETE_TRAVEL_EXTENSION_MUTATION,
  ADD_TRAVEL_EXTENSION_HIGHLIGHTS_MUTATION,
  REMOVE_TRAVEL_EXTENSION_HIGHLIGHTS_MUTATION,
  CREATE_TRAVEL_EXTENSION_HIGHLIGHTS_MUTATION,
  DELETE_TRAVEL_EXTENSION_HIGHLIGHTS_MUTATION,
  UPDATE_TRAVEL_EXTENSION_HIGHLIGHTS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const travelExtensionRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_TRAVEL_EXTENSION_QUERY, variables).then((data: any) => {
      return hydrateObject(data.travelExtension)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_TRAVEL_EXTENSIONS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.travelExtensions)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_TRAVEL_EXTENSION_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.travelExtension),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_TRAVEL_EXTENSIONS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.travelExtensions),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(TRAVEL_EXTENSIONS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([TRAVEL_EXTENSIONS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_TRAVEL_EXTENSION_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.travelExtension)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_TRAVEL_EXTENSION_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.travelExtension)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_TRAVEL_EXTENSION_MUTATION, variables).then((data: any) => {
      return data.deleteTravelExtension
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_TRAVEL_EXTENSION_MUTATION, variables).then((data: any) => {
      return data.forceDeleteTravelExtension
    })
  },

  assignHighlights: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        highlightIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_EXTENSION_HIGHLIGHTS_MUTATION, variables)
  },

  unassignHighlights: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        highlightIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_EXTENSION_HIGHLIGHTS_MUTATION, variables)
  },

  createHighlights: async (id: string, items: Array<travelHighlightObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        highlights: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_EXTENSION_HIGHLIGHTS_MUTATION, variables)
  },

  deleteHighlights: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        highlightIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_EXTENSION_HIGHLIGHTS_MUTATION, variables)
  },

  updateHighlightsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        highlightIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_EXTENSION_HIGHLIGHTS_ORDER_MUTATION, variables)
  },
}

export default travelExtensionRepository
